import { Instant } from "@js-joda/core";
import { MappedValue } from "components/basic/dataset-display-value/DatasetDisplayValue";
import { HighlightColor, HighlightRange } from "components/dashboard/product-template-table/utils/interfaces";
import { getValueColors, styleHandler } from "components/dashboard/product-template-table/utils/utils";
import useCopyDataset from "hooks/useCopyDataset";
import useTimerThreshold from "hooks/useTimerThreshold";
import React, { useEffect, useMemo, useRef, useState } from "react";
import useChartDialogStore from "store/chart-dialog-store";
import useDatasetIdentifiersStore from "store/dataset-identifiers-store";

const DatasetDisplay = ({
  identifier,
  identifiers,
  useAverage,
  value,
  unit,
  title,
  stringValueMapper,
  decimalPlaces,
  time,
  maxAge,
  highlightColor,
  bgHighlightRanges,
  onHighlightColors,
  hideValue,
  setPointDatasets,
  customBackground,
}: {
  identifier?: string;
  identifiers?: string[];
  useAverage?: boolean;
  value?: number;
  unit?: string;
  title?: string;
  stringValueMapper?: number;
  decimalPlaces?: number;
  time: Instant | null;
  maxAge?: number;
  highlightColor?: HighlightColor | null;
  bgHighlightRanges?: HighlightRange[] | null;
  onHighlightColors?: (color?: string, bgColor?: string, colorEntireRow?: boolean) => void;
  hideValue?: boolean;
  setPointDatasets?: string[] | null;
  customBackground?: string | null;
}) => {
  const [isHovered, setIsHovered] = useState(false);
  useCopyDataset(isHovered, title, identifier);
  const prev = useRef<{ color?: string; bgColor?: string; colorEntireRow?: boolean }>({});
  const datasetTimestamp = time?.toEpochMilli() ?? 0;

  const isInitiallyExpired =
    maxAge && datasetTimestamp > 0 ? Instant.now().toEpochMilli() - datasetTimestamp > maxAge * 1000 : false;

  const { isExpired: timerExpired, resetTimer } = useTimerThreshold(maxAge, datasetTimestamp);

  const isExpired = isInitiallyExpired || timerExpired;

  useEffect(() => {
    resetTimer(datasetTimestamp);
  }, [datasetTimestamp, resetTimer]);

  const selectedIdsStore = useDatasetIdentifiersStore();
  const { openDialog } = useChartDialogStore();

  const handleClick = (e: React.MouseEvent) => {
    if (e.ctrlKey && identifier) {
      if (selectedIdsStore.ids.has(identifier)) {
        selectedIdsStore.removeIdentifier(identifier);
      } else {
        selectedIdsStore.addIdentifier(identifier);
      }
    } else {
      selectedIdsStore.clear();
    }
  };
  const [color, bgColor, colorEntireRow] = useMemo(
    () =>
      getValueColors(value as number, {
        highlightRanges: bgHighlightRanges,
        defaultHighlightColor: highlightColor,
      }),
    [value, bgHighlightRanges, highlightColor]
  );

  const rowHighlightLocked = useRef(false);

  if (onHighlightColors) {
    if (isExpired) {
      if (prev.current.color || prev.current.bgColor || prev.current.colorEntireRow) {
        prev.current = {};
        rowHighlightLocked.current = false;
        queueMicrotask(() => onHighlightColors(undefined, undefined, false));
      }
    } else {
      const changed =
        prev.current.color !== color ||
        prev.current.bgColor !== bgColor ||
        prev.current.colorEntireRow !== colorEntireRow;

      if (changed) {
        prev.current = { color, bgColor, colorEntireRow };

        if (colorEntireRow && (color || bgColor)) {
          rowHighlightLocked.current = true;
          queueMicrotask(() => onHighlightColors(color, bgColor, true));
        }
      }
    }
  }

  const handleDoubleClick = () => {
    if (setPointDatasets && setPointDatasets.length > 0) {
      setPointDatasets.forEach(dataset => {
        selectedIdsStore.addIdentifier(dataset);
      });
    }
    if (useAverage && identifiers?.length && identifiers.length > 0) {
      identifiers.forEach(id => selectedIdsStore.addIdentifier(id));
    } else if (identifier) {
      selectedIdsStore.addIdentifier(identifier);
    }
    openDialog();
  };

  const datasetValueTemplate = () => {
    if (hideValue) {
      return null;
    }
    return stringValueMapper ? (
      <MappedValue mapValue={value} mapperId={stringValueMapper} color={color} bgColor={bgColor} />
    ) : (
      <DatasetDisplayValue
        value={value}
        unit={unit}
        decimalPlaces={decimalPlaces}
        color={color}
        bgColor={bgColor}
      />
    );
  };

  const expiredValueTemplate = () => {
    if (hideValue) {
      return <div />;
    }
    return <span>-</span>;
  };

  const isIdentifierSelected = identifier && selectedIdsStore.ids.has(identifier);

  const getBgColor = () => {
    if (customBackground) {
      return customBackground;
    }
    if (hideValue && bgColor && !isExpired) {
      return bgColor;
    }
    return "inherit";
  };

  return (
    <div
      className="p-inputtext"
      style={{
        border: isIdentifierSelected ? "1px solid #007df2" : "none",
        borderRadius: "5px",
        background: getBgColor(),
        color: "inherit",
        whiteSpace: "pre-wrap",
        padding: hideValue ? "0.75rem" : undefined,
      }}
      onClick={handleClick}
      onDoubleClick={handleDoubleClick}
      onPointerEnter={() => setIsHovered(true)}
      onPointerLeave={() => setIsHovered(false)}
    >
      {isExpired ? expiredValueTemplate() : datasetValueTemplate()}
    </div>
  );
};

export default DatasetDisplay;

const DatasetDisplayValue = ({
  value,
  unit,
  decimalPlaces,
  color,
  bgColor,
}: {
  value: number | undefined;
  unit: string | undefined;
  decimalPlaces?: number;
  color?: string;
  bgColor?: string;
}) => {
  return (
    value != null && (
      <div
        className="white-space-nowrap"
        style={styleHandler(color, bgColor)}
      >{`${value.toFixed(decimalPlaces ?? 2)} ${unit}`}</div>
    )
  );
};
